// @givmo/app-charity — backend-OTP email verification (/verify-email).
//
// Two kinds of arrival, and the screen has to be honest with both:
//   - the SIGN-UP flow, which already fired the OTP before navigating here (`otpSent`, or
//     `otpSendFailed` when that send lost the race);
//   - a GUARD redirect with no prior send — since 2026-08-18 that means a microsoft.com user, whom
//     GCIP mints with `emailVerified: false` (see `needsBackendOtpVerification` in @givmo/auth),
//     and since NF-FIX24 an app account whose email was never proven (signed up by phone, or
//     changed in an app since NF-FIX19). For them the screen AUTO-SENDS once on mount, so "Enter
//     the code we emailed you" is true by the time they read it. A Google/Apple user whose own
//     address that provider carries attests and never lands here;
//   - the SIGN-UP flow again, but mounted by the GUARD rather than by the form (CPA-WEB59): the
//     session's publish re-runs `/sign-up/email`'s public-route redirect, which lands here with
//     only `redirect` — the form's `otpSent` flag arrives afterwards, on this already-mounted
//     screen (or, under a fast round trip, nested inside `redirect`). A send the app already
//     started counts as the prior send (`otpSendAttempted`, recorded by the auth context the
//     moment `sendOtp` is called), or this mount would fire a SECOND code and the backend's resend
//     throttle would turn it into a false "couldn't send the first code".
//
// The user enters the 6-digit code the backend emailed; on success we route onward. A "Resend code"
// affordance re-fires the OTP. This is the SAME backend OTP the admin app uses (generic `/users/me/*`
// endpoints), not a Firebase email link.
//
// A BOARD DIRECTOR CONFIRMS HERE TOO (CBG-SEC2 SEC2-WEB, contract §4.3; mockup `m2-email-confirm`;
// D-CBG-18: "a prop rather than a copy"). The logic — the auto-send once, the cooldown, the
// confirm, the resend, the redirect — is shared. The director arrival renders in Board's director
// shell: six boxes, "Confirm", and one row with the resend beside "Use a different email", which
// signs out and returns to where the director was going. It carries no funnel box: a director is
// not claiming an organization. Its line says the code was sent only once a send is known to have
// happened. The charity arrival keeps its words, its box and its row exactly as shipped.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardBody,
  OtpField,
  TextField,
  ErrorMessage,
  testIdProp,
} from '@givmo/design-system';
import { DirectorShell } from '@givmo/board';
import { useAuth } from '../../auth/auth-context';
import { AuthShell } from './auth-shell';
import { useAuthError } from './use-auth-error';
import { sanitizeRedirectTarget } from '../../router/redirect-target';
import { SIGN_IN_LANDING_STATE } from '../../console/sidebar-frame';
import { DEFAULT_AUTHENTICATED_ROUTE } from './auth-routes';
import { FUNNEL_STEP_FIND, PRODUCT_NAME } from '../../lib/funnel-copy';
import { DIRECTOR_ROUTES } from '../board/director-routes';
import { DIRECTOR_AUTH_LINK } from './director-arrival';

/** The resend link while it cools down: the same link, disabled, in the quiet grey (`m2-email-confirm`). */
const COOLING =
  'cursor-default font-sans text-s13 font-w600 tracking-givmo text-neutral-400 outline-none';

export interface VerifyEmailScreenProps {
  /** Who arrived: the console's own confirmation (the default, unchanged) or a board director. */
  arrival?: 'console' | 'director';
}

export function VerifyEmailScreen({ arrival = 'console' }: VerifyEmailScreenProps = {}) {
  const director = arrival === 'director';
  const { user, confirmOtp, sendOtp, signOut, otpSendAttempted } = useAuth();
  const navigate = useNavigate();
  // Two routes render this screen, so the search read is non-strict by necessity.
  const { redirect, otpSent, otpSendFailed } = useSearch({ strict: false }) as {
    redirect?: string;
    otpSent?: boolean;
    otpSendFailed?: boolean;
  };
  const { error, clearError, surface } = useAuthError();
  // Whether this screen's own auto-send went out — with the sign-up form's `otpSent` and a resend,
  // what the director arrival's line may claim.
  const [autoSent, setAutoSent] = useState(false);

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendPending, setResendPending] = useState(false);
  // No prior send happened when neither flag rides the search — a guard arrival (provider-OTP
  // user, or a reload). The auto-send below covers it, so the cooldown starts as if sent.
  const [cooldown, setCooldown] = useState(() => (otpSendFailed ? 0 : 30));

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  // Auto-send exactly once per mount for arrivals with no prior send. A failure surfaces through
  // the same notice the sign-up flow's failed first send uses, and zeroes the cooldown so Resend
  // is immediately available. The ref (not state) guards React 18 double-invoke in dev.
  const autoSendStarted = useRef(false);
  const [autoSendFailed, setAutoSendFailed] = useState(false);
  useEffect(() => {
    if (otpSent || otpSendFailed || autoSendStarted.current) return;
    // A send this instance already started — the sign-up form's — is the prior send, whatever the
    // URL says yet (see the header).
    if (otpSendAttempted()) return;
    autoSendStarted.current = true;
    void (async () => {
      try {
        await sendOtp();
        setAutoSent(true);
      } catch {
        setAutoSendFailed(true);
        setCooldown(0);
      }
    })();
  }, [otpSent, otpSendFailed, sendOtp, otpSendAttempted]);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = code.trim();
      if (trimmed.length === 0 || submitting) return;
      clearError();
      setSubmitting(true);
      try {
        await confirmOtp(trimmed);
        await navigate({
          to:
            sanitizeRedirectTarget(redirect) ??
            (director ? DIRECTOR_ROUTES.home : DEFAULT_AUTHENTICATED_ROUTE),
          state: SIGN_IN_LANDING_STATE,
        });
      } catch (thrown) {
        surface(thrown);
      } finally {
        setSubmitting(false);
      }
    },
    [code, submitting, clearError, confirmOtp, navigate, redirect, surface, director],
  );

  const onResend = useCallback(async () => {
    if (resendPending || cooldown > 0) return;
    clearError();
    setResent(false);
    setResendPending(true);
    try {
      await sendOtp();
      setResent(true);
      setCooldown(30);
    } catch (thrown) {
      surface(thrown);
    } finally {
      setResendPending(false);
    }
  }, [resendPending, cooldown, clearError, sendOtp, surface]);

  const sendFailed = otpSendFailed === true || autoSendFailed;
  const notices = (
    <>
      {sendFailed ? (
        <ErrorMessage testId="auth-otp-send-notice">
          We couldn’t send the first code. Use Resend code below to try again.
        </ErrorMessage>
      ) : null}
      {/* B1: the resend is an async action whose ONLY outcome is this line, so it is announced. */}
      {resent && (
        <p
          role="status"
          className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary"
          {...testIdProp('auth-otp-resent')}
        >
          A new code is on its way.
        </p>
      )}
      {error !== null && <ErrorMessage testId="auth-error">{error}</ErrorMessage>}
    </>
  );

  if (director) {
    const address = user?.email ?? null;
    const sendKnown = otpSent === true || autoSent || resent;
    return (
      <DirectorShell
        title="Confirm your email"
        description={
          address === null || sendFailed
            ? undefined
            : sendKnown
              ? `We sent a 6-digit code to ${address}.`
              : `We’re sending a 6-digit code to ${address}.`
        }
        testId="page-director-email-confirm"
      >
        <Card className="w-full max-w-[440px]" testId="director-email-confirm">
          <CardBody className="flex flex-col gap-16">
            <form className="flex flex-col gap-16" onSubmit={(e) => void onSubmit(e)} noValidate>
              <OtpField
                value={code}
                onValueChange={setCode}
                error={error !== null}
                aria-label="6-digit code"
                testId="auth-otp-input"
              />
              {notices}
              <Button
                type="submit"
                appearance="saas"
                tone="primary"
                size="lg"
                fullWidth
                disabled={code.trim().length < 6}
                loading={submitting}
                testId="auth-otp-submit"
              >
                Confirm
              </Button>
            </form>
            {/* One row: the resend, cooling then a link, beside the way out to another address. */}
            <div className="flex items-center justify-between gap-12 border-t border-border-subtle pt-16">
              <button
                type="button"
                onClick={() => void onResend()}
                disabled={cooldown > 0 || resendPending}
                className={
                  cooldown > 0 ? COOLING : `${DIRECTOR_AUTH_LINK} font-sans text-s13 tracking-givmo`
                }
                {...testIdProp('auth-otp-resend')}
              >
                {cooldown > 0 ? (
                  <>
                    Resend in <span className="tabular-nums">{cooldown}</span>s
                  </>
                ) : (
                  'Resend code'
                )}
              </button>
              <button
                type="button"
                onClick={() =>
                  void (async () => {
                    await signOut();
                    const target = sanitizeRedirectTarget(redirect);
                    await navigate({ to: target ?? DIRECTOR_ROUTES.signIn, replace: true });
                  })()
                }
                className={`${DIRECTOR_AUTH_LINK} font-sans text-s13 tracking-givmo`}
                {...testIdProp('director-auth-different-email')}
              >
                Use a different email
              </button>
            </div>
          </CardBody>
        </Card>
      </DirectorShell>
    );
  }

  return (
    <AuthShell testId="page-verify-email">
      <div className="flex flex-col gap-4">
        <h1 className="font-sans text-s22 font-w600 tracking-givmo text-text-strong">
          Verify your email
        </h1>
        <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
          Enter the code we emailed you. This is the address {PRODUCT_NAME} will use for everything
          about your organization&rsquo;s grants.
        </p>
      </div>

      <form className="flex flex-col gap-16" onSubmit={(e) => void onSubmit(e)} noValidate>
        <TextField
          label="Verification code"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          testId="auth-otp-input"
        />

        {notices}

        <Button
          type="submit"
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          disabled={code.trim().length === 0}
          loading={submitting}
          testId="auth-otp-submit"
        >
          Verify
        </Button>
      </form>

      {/* The wait is not dead time — say what it buys. Without this the OTP screen is the one point in
          the funnel where a caller is asked to stop and has been told nothing about what comes next. */}
      <p
        className="rounded-lg bg-surface-sunken p-16 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
        {...testIdProp('auth-otp-next-step')}
      >
        <span className="font-w600 text-text-strong">
          Next: {FUNNEL_STEP_FIND.title.toLowerCase()}.
        </span>{' '}
        {FUNNEL_STEP_FIND.detail} Then you confirm you&rsquo;re authorized to act for it, and set up
        how it receives grants.
      </p>

      <div className="flex items-center justify-between border-t border-border-subtle pt-20">
        <Button
          type="button"
          onClick={() => void onResend()}
          appearance="saas"
          tone="ghost"
          size="sm"
          loading={resendPending}
          disabled={cooldown > 0}
          testId="auth-otp-resend"
        >
          {cooldown > 0 ? (
            <>
              Resend in <span className="tabular-nums">{cooldown}</span>s
            </>
          ) : (
            'Resend code'
          )}
        </Button>
        <button
          type="button"
          onClick={() => void signOut()}
          className="font-sans text-s13 font-w500 tracking-givmo text-text-tertiary outline-none transition-colors hover:text-text-secondary focus-visible:underline"
          {...testIdProp('auth-otp-signout')}
        >
          Sign out
        </button>
      </div>
    </AuthShell>
  );
}
